import { INTERESTS } from '@/lib/contact-interests';
import searchContent from '@/lib/search-content.json';
import React, { useState, useRef, useEffect } from "react";
import { useDocumentMeta } from "../lib/useDocumentMeta";
import { SEO_ROUTES } from "../lib/seo";
import { trackLead } from "../lib/analytics";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail,
  Phone,
  MapPin,
  Send,
  Loader2,
  Video,
  HardHat,
  ArrowRight,
  Quote,
  Flame,
  Clock,
  CheckCircle2,
  Inbox,
  ClipboardCheck,
  Compass,
} from "lucide-react";
import { COMPANY, ROLES, MEDIA } from "@/lib/site-data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${(BACKEND_URL || "").replace(/\/$/, "")}/api`;



const INITIAL = {
  name: "",
  email: "",
  company: "",
  phone: "",
  role: "",
  service: "",
  message: "",
};

const NEXT_STEPS = [
  {
    icon: Inbox,
    step: "01",
    title: "We Read Every Message.",
    body: "Our team reviews your request and brings in the right product or field specialist.",
  },
  {
    icon: Clock,
    step: "02",
    title: "Connect with the Right Team.",
    body: "We respond within 24 hours. Weekend inquiries receive a response by 3 p.m. Eastern the next business day.",
  },
  {
    icon: Compass,
    step: "03",
    title: "Review the Fit.",
    body: "We discuss your goals, demonstrate relevant software, or review the service requirements.",
  },
  {
    icon: ClipboardCheck,
    step: "04",
    title: "Agree on the Next Step.",
    body: "If we can help, we scope a path. If we can't, we tell you straight and point you at someone who can.",
  },
];

export default function Contact() {
  useDocumentMeta(SEO_ROUTES["/contact"]);
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(INITIAL);
  useEffect(() => { const interest = searchParams.get("interest") || searchParams.get("service"); const matchingInterest = INTERESTS.find(item => item.toLocaleLowerCase() === (interest || "").toLocaleLowerCase()); if (matchingInterest) setForm(current => ({...current, service: matchingInterest})); }, [searchParams]);
  const [submitting, setSubmitting] = useState(false);
  const [humanCheck, setHumanCheck] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot — real users never fill this
  const renderedAtMs = useRef(Date.now());

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Name, email, and message are required.");
      return;
    }
    if (!humanCheck) {
      toast.error("Please confirm you're human before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const messageParts = [];
      if (form.role) messageParts.push(`Role: ${form.role}`);
      messageParts.push(form.message.trim());
      const mergedMessage = messageParts.join("\n\n");

      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        message: mergedMessage,
        company: form.company.trim() || null,
        phone: form.phone.trim() || null,
        service: form.service || null,
        // Anti-spam fields
        website: website || null,
        human_check: humanCheck,
        rendered_at_ms: renderedAtMs.current,
      };
      const response = await axios.post(`${API}/contact`, payload);
      if (!website && humanCheck && response.status === 201 && response.data?.id) {
        trackLead(form.service, response.data.id);
      }
      toast.success("Message received. We'll be in touch shortly.");
      setForm(INITIAL);
      setHumanCheck(false);
      renderedAtMs.current = Date.now();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Unable to submit your inquiry. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="w-full">
      {/* ============ HERO — OPERATIONAL HQ (looping silent field clip · Iter 8.0) ============ */}
      <section
        data-testid="contact-hero"
        className="grain-overlay relative min-h-[62vh] flex items-end border-b border-zinc-900 overflow-hidden"
      >
        {/* Silent looping ambient field clip — quieter, slower than the blade hero */}
        <video
          data-testid="contact-hero-video"
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          poster={MEDIA.fieldClipPoster}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "brightness(0.42) contrast(1.18) saturate(0.82)" }}
          aria-hidden="true"
        >
          <source src={MEDIA.fieldClipMp4} type="video/mp4" />
        </video>

        {/* Cinematic gradients — restrained, command-center calm */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/55 to-black pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/35 to-black/60 pointer-events-none" />
        <div className="absolute -bottom-40 -right-40 w-[700px] h-[700px] bg-[radial-gradient(circle,rgba(255,107,26,0.13)_0%,transparent_65%)] pointer-events-none" />

        {/* Operational HQ corner stamp — quiet authority signal */}
        <div
          data-testid="contact-hq-stamp"
          className="absolute top-6 right-6 lg:top-10 lg:right-10 flex items-center gap-3 z-20"
        >
          <span
            className="relative inline-flex items-center justify-center h-2 w-2"
            aria-label="Live operational signal"
          >
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#ff6b1a] opacity-70 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#ff6b1a] shadow-[0_0_12px_rgba(255,107,26,0.85)]" />
          </span>
          <span className="font-display uppercase tracking-[0.5em] text-[10px] md:text-[11px] text-[#ff6b1a]">
            ForgedOps · Operational HQ
          </span>
        </div>

        {/* Embers */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
          {[
            { l: "12%", b: "8%", d: 0, dr: "20px" },
            { l: "32%", b: "5%", d: 1.5, dr: "-30px" },
            { l: "55%", b: "12%", d: 2.7, dr: "40px" },
            { l: "78%", b: "8%", d: 1.0, dr: "-25px" },
            { l: "92%", b: "14%", d: 3.6, dr: "30px" },
          ].map((e, i) => (
            <span
              key={i}
              className="ember"
              style={{
                left: e.l,
                bottom: e.b,
                animationDelay: `${e.d}s`,
                "--drift": e.dr,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-8 pt-32 pb-20 w-full">
          <div className="col-span-12 lg:col-span-10 flex flex-col gap-7">
            <div className="flex items-center gap-4">
              <span className="inline-block w-2 h-2 bg-[#ff6b1a]" />
              <span className="font-display uppercase tracking-[0.45em] text-[11px] text-[#ff6b1a]">
                Operations Inquiry
              </span>
            </div>
            <h1
              data-testid="contact-title"
              className="font-display uppercase text-[clamp(2.6rem,7vw,6rem)] leading-[0.88] font-black tracking-tight text-balance drop-shadow-[0_4px_30px_rgba(0,0,0,0.7)]"
            >
              Tell Us What You
              <br />
              <span className="metallic-text">Want to Build Next.</span>
            </h1>
            <p className="text-zinc-200 text-lg md:text-2xl leading-[1.4] max-w-3xl font-light">
              Show us the problem, the project, or the next move for your company. We’ll connect you with the software, field technology, training, consulting, or branding that fits the work.
            </p>
            <div className="flex flex-wrap items-center gap-6 pt-3">
              <div className="flex items-center gap-2 text-zinc-400 text-sm">
                <Clock size={14} className="text-[#ff6b1a]" />
                <span className="font-display uppercase tracking-[0.2em] text-[11px]">
                  Direct team contact
                </span>
              </div>
              <span className="h-3 w-px bg-zinc-700" />
              <div className="flex items-center gap-2 text-zinc-400 text-sm">
                <CheckCircle2 size={14} className="text-[#ff6b1a]" />
                <span className="font-display uppercase tracking-[0.2em] text-[11px]">
                  Software demos & service scoping
                </span>
              </div>
              <span className="h-3 w-px bg-zinc-700" />
              <div className="flex items-center gap-2 text-zinc-400 text-sm">
                <HardHat size={14} className="text-[#ff6b1a]" />
                <span className="font-display uppercase tracking-[0.2em] text-[11px]">
                  Phone · Virtual · Onsite
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FORM + INFO RAIL ============ */}
      <section className="py-24 border-b border-zinc-900 bg-[#050505] relative overflow-hidden">
        <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] bg-[radial-gradient(circle,rgba(255,107,26,0.08)_0%,transparent_65%)] pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-10 relative z-10">
          {/* Info rail */}
          <div className="col-span-12 lg:col-span-4 flex flex-col gap-10">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <span className="h-px w-10 bg-[#ff6b1a]" />
                <span className="font-display uppercase tracking-[0.4em] text-[10px] text-[#ff6b1a]">
                  Direct lines
                </span>
              </div>
              <h2 className="font-display uppercase text-3xl md:text-4xl font-black tracking-tight leading-[0.92]">
                Prefer to Reach Out
                <br />
                <span className="text-zinc-500">Direct?</span>
              </h2>
            </div>

            <ul className="flex flex-col gap-px bg-zinc-900 border border-zinc-900">
              <li className="ops-card bg-[#141417] p-5 flex items-start gap-4">
                <span className="w-11 h-11 border border-[#ff6b1a]/40 bg-[#ff6b1a]/5 flex items-center justify-center flex-shrink-0">
                  <MapPin size={16} className="text-[#ff6b1a]" />
                </span>
                <div className="flex flex-col gap-0.5">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    Based in
                  </div>
                  <div className="text-white font-display tracking-wide">
                    {COMPANY.location}
                  </div>
                  <div className="text-zinc-500 text-xs mt-0.5">
                    {COMPANY.coverage}
                  </div>
                </div>
              </li>
              <li className="ops-card bg-[#141417] p-5 flex items-start gap-4">
                <span className="w-11 h-11 border border-[#ff6b1a]/40 bg-[#ff6b1a]/5 flex items-center justify-center flex-shrink-0">
                  <Mail size={16} className="text-[#ff6b1a]" />
                </span>
                <div className="flex flex-col gap-0.5">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    Email
                  </div>
                  <a
                    href={`mailto:${COMPANY.email}`}
                    data-testid="contact-email-link"
                    className="text-white hover:text-[#ff6b1a] transition-colors break-all"
                  >
                    {COMPANY.email}
                  </a>
                </div>
              </li>
              <li className="ops-card bg-[#141417] p-5 flex items-start gap-4">
                <span className="w-11 h-11 border border-[#ff6b1a]/40 bg-[#ff6b1a]/5 flex items-center justify-center flex-shrink-0">
                  <Phone size={16} className="text-[#ff6b1a]" />
                </span>
                <div className="flex flex-col gap-0.5">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    Phone
                  </div>
                  <a
                    href={`tel:${COMPANY.phone.replace(/[^0-9+]/g, "")}`}
                    data-testid="contact-phone-link"
                    className="text-white hover:text-[#ff6b1a] transition-colors"
                  >
                    {COMPANY.phone}
                  </a>
                </div>
              </li>
            </ul>

            {/* Engagement modes */}
            <div className="border border-[#ff6b1a]/30 bg-gradient-to-br from-[#0e0e10] to-[#141417] p-6 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-[200px] h-[200px] bg-[radial-gradient(circle,rgba(255,107,26,0.15)_0%,transparent_65%)] pointer-events-none" />
              <div className="relative z-10">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#ff6b1a] mb-4">
                  <Flame size={14} />
                  <span>Engagement modes</span>
                </div>
                <ul className="flex flex-col gap-3">
                  <li className="flex items-center gap-3 text-zinc-200 text-sm">
                    <Phone size={14} className="text-[#ff6b1a] flex-shrink-0" />
                    <span className="font-display uppercase tracking-[0.18em] text-[12px]">
                      Phone advisory
                    </span>
                  </li>
                  <li className="flex items-center gap-3 text-zinc-200 text-sm">
                    <Video size={14} className="text-[#ff6b1a] flex-shrink-0" />
                    <span className="font-display uppercase tracking-[0.18em] text-[12px]">
                      Virtual meetings
                    </span>
                  </li>
                  <li className="flex items-center gap-3 text-zinc-200 text-sm">
                    <HardHat size={14} className="text-[#ff6b1a] flex-shrink-0" />
                    <span className="font-display uppercase tracking-[0.18em] text-[12px]">
                      Onsite consulting
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Response time */}
            <div className="border border-zinc-900 bg-[#0a0a0b] p-6">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                <Clock size={12} className="text-[#ff6b1a]" /> Prefer a direct conversation?
              </div>
              <div className="font-display text-3xl md:text-4xl font-black tracking-tight metallic-text">
                Let’s define your next step
              </div>
              <p className="text-zinc-400 text-sm mt-3 leading-relaxed">
                Call direct to discuss your software or service needs.</p>
              <p className="text-zinc-400 text-sm mt-3 leading-relaxed">{searchContent.consultationTerms}</p>
              <p className="text-zinc-400 text-sm mt-3 leading-relaxed">For models, takeoffs, or cut/fill analysis, tell us which current drawings, CAD, or model files you have and your preferred due date. We’ll arrange file transfer and quote the work.</p>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="col-span-12 lg:col-span-8 border border-zinc-900 bg-gradient-to-br from-[#0a0a0c] to-[#080809] p-6 md:p-10 flex flex-col gap-6 relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#ff6b1a] to-transparent" />

            <div className="flex items-center gap-3 mb-2">
              <span className="h-px w-8 bg-[#ff6b1a]" />
              <span className="font-display uppercase tracking-[0.4em] text-[10px] text-[#ff6b1a]">
                Tell us about the work
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field
                label="Full Name *"
                id="name"
                testid="contact-input-name"
                value={form.name}
                onChange={update("name")}
                placeholder="John Smith"
                required
              />
              <Field
                label="Email *"
                id="email"
                type="email"
                testid="contact-input-email"
                value={form.email}
                onChange={update("email")}
                placeholder="you@company.com"
                required
              />
              <Field
                label="Company"
                id="company"
                testid="contact-input-company"
                value={form.company}
                onChange={update("company")}
                placeholder="Company / Agency"
              />
              <Field
                label="Phone"
                type="tel" id="phone"
                testid="contact-input-phone"
                value={form.phone}
                onChange={update("phone")}
                placeholder="(555) 555-5555"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="role"
                  className="font-display uppercase tracking-[0.3em] text-[11px] text-zinc-400"
                >
                  Your Role
                </label>
                <select
                  id="role"
                  data-testid="contact-input-role"
                  value={form.role}
                  onChange={update("role")}
                  className="bg-[#050506] border border-zinc-800 text-white px-4 py-3.5 focus:outline-none focus:border-[#ff6b1a] focus:ring-1 focus:ring-[#ff6b1a]/30 transition-all"
                >
                  <option value="">Select Your Role (Optional)</option>
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="service"
                  className="font-display uppercase tracking-[0.3em] text-[11px] text-zinc-400"
                >
                  Product or Service of Interest
                </label>
                <select
                  id="service"
                  data-testid="contact-input-service"
                  value={form.service}
                  onChange={update("service")}
                  className="bg-[#050506] border border-zinc-800 text-white px-4 py-3.5 focus:outline-none focus:border-[#ff6b1a] focus:ring-1 focus:ring-[#ff6b1a]/30 transition-all"
                >
                  <option value="">Choose an Interest (Optional)</option>
                  {INTERESTS.map((interest) => <option key={interest} value={interest}>{interest}</option>)}
                </select>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="message"
                className="font-display uppercase tracking-[0.3em] text-[11px] text-zinc-400"
              >
                What Do You Need Help With? *
              </label>
              <textarea
                id="message"
                data-testid="contact-input-message"
                value={form.message}
                onChange={update("message")}
                placeholder="Tell us about your goals, products or services, team size, location, and timing. For training, include the course and crew size."
                rows={6}
                required
                minLength={5}
                className="bg-[#050506] border border-zinc-800 text-white px-4 py-3.5 focus:outline-none focus:border-[#ff6b1a] focus:ring-1 focus:ring-[#ff6b1a]/30 transition-all resize-y placeholder:text-zinc-600"
              />
            </div>

            {/* Honeypot — invisible to humans, bots fill it. Iter 8.3 */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-9999px",
                top: "-9999px",
                width: "1px",
                height: "1px",
                overflow: "hidden",
                opacity: 0,
                pointerEvents: "none",
              }}
            >
              <label htmlFor="website">Website (Leave Blank)</label>
              <input
                type="text"
                id="website"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                style={{ width: "1px", height: "1px" }}
              />
            </div>

            {/* Human verification — explicit consent + spam guard. Iter 8.3 */}
            <label
              data-testid="contact-human-check"
              className="group flex items-start gap-3 cursor-pointer select-none border border-zinc-800 bg-[#050506] px-4 py-4 hover:border-zinc-700 transition-all"
            >
              <input
                type="checkbox"
                checked={humanCheck}
                onChange={(e) => setHumanCheck(e.target.checked)}
                className="mt-[3px] h-4 w-4 flex-shrink-0 appearance-none border border-zinc-600 bg-black checked:bg-[#ff6b1a] checked:border-[#ff6b1a] cursor-pointer transition-all relative
                  checked:after:content-['✓'] checked:after:absolute checked:after:inset-0 checked:after:flex checked:after:items-center checked:after:justify-center checked:after:text-black checked:after:text-[11px] checked:after:font-black"
              />
              <span className="flex flex-col gap-0.5">
                <span className="font-display uppercase tracking-[0.18em] text-[12px] font-bold text-white">
                  I'm a human, not a bot.
                </span>
                <span className="text-[11px] text-zinc-500 leading-snug">
                  Please confirm before sending your inquiry.
                </span>
              </span>
            </label>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2 border-t border-zinc-900">
              <p className="text-xs text-zinc-500 leading-relaxed max-w-md">
                By submitting this form you consent to being contacted about
                your inquiry. See our <Link to="/privacy" className="underline text-zinc-300">Privacy Policy</Link> for how we handle your information.
              </p>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={submitting || !humanCheck}
                className="group inline-flex items-center gap-3 px-8 py-4 bg-[#ff6b1a] text-black font-display uppercase tracking-[0.18em] text-[13px] font-bold hover:bg-[#ff8533] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_0_40px_rgba(255,107,26,0.35)] hover:shadow-[0_0_60px_rgba(255,107,26,0.55)]"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending
                  </>
                ) : (
                  <>
                    Send Inquiry
                    <Send size={14} />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* ============ WHAT HAPPENS NEXT ============ */}
      <section className="py-24 border-b border-zinc-900">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-10 items-start">
          <div className="col-span-12 lg:col-span-5 flex flex-col gap-6">
            <div className="flex items-center gap-3">
              <span className="h-px w-10 bg-[#ff6b1a]" />
              <span className="font-display uppercase tracking-[0.4em] text-[10px] text-[#ff6b1a]">
                What happens next
              </span>
            </div>
            <h2 className="font-display uppercase text-4xl md:text-5xl font-black leading-[0.92] tracking-tight">
              A Clear Path.
              <br />
              <span className="metallic-text">A Direct Conversation.</span>
            </h2>
            <p className="text-zinc-400 leading-relaxed max-w-md">
              Every inquiry hits a real person on our ops team. Here's the
              sequence after you press send.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
              {NEXT_STEPS.map((s) => {
                const Icon = s.icon;
                return (
                  <li
                    key={s.step}
                    data-testid={`contact-next-${s.step}`}
                    className="ops-card group bg-[#141417] p-7 flex flex-col gap-4 min-h-[220px] border-t-2 border-t-transparent hover:border-t-[#ff6b1a] transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="w-11 h-11 border border-[#ff6b1a]/40 bg-[#ff6b1a]/5 flex items-center justify-center group-hover:bg-[#ff6b1a]/10 transition-colors">
                        <Icon size={18} className="text-[#ff6b1a]" />
                      </div>
                      <span className="font-display text-xs tracking-[0.3em] uppercase text-zinc-600">
                        {s.step}
                      </span>
                    </div>
                    <h3 className="font-display uppercase text-lg md:text-xl font-bold tracking-tight leading-tight">
                      {s.title}
                    </h3>
                    <p className="text-zinc-400 text-sm leading-relaxed">
                      {s.body}
                    </p>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      {/* ============ CLOSING PULL QUOTE ============ */}
      <section className="relative py-28 overflow-hidden">

        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/85 to-black/95" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[radial-gradient(circle,rgba(255,107,26,0.18)_0%,transparent_60%)] pointer-events-none" />
        <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-10 flex flex-col items-center text-center gap-8">
          <Quote className="text-[#ff6b1a]/40" size={56} strokeWidth={1.2} />
          <p className="font-display text-3xl md:text-5xl font-black leading-[1.1] tracking-tight text-balance max-w-4xl">
            Build your next step with a team that understands the work.
          </p>
          <Link
            to="/about"
            data-testid="contact-closing-link"
            className="font-display uppercase tracking-[0.35em] text-xs text-zinc-400 hover:text-[#ff6b1a] transition-colors flex items-center gap-2"
          >
            Read the ForgedOps Story <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}

function Field({ label, id, testid, value, onChange, placeholder, type = "text", required }) {
  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={id}
        className="font-display uppercase tracking-[0.3em] text-[11px] text-zinc-400"
      >
        {label}
      </label>
      <input
        id={id}
        name={id}
        autoComplete={{name:"name",email:"email",company:"organization",phone:"tel"}[id]}
        type={type}
        data-testid={testid}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="bg-[#050506] border border-zinc-800 text-white px-4 py-3.5 focus:outline-none focus:border-[#ff6b1a] focus:ring-1 focus:ring-[#ff6b1a]/30 transition-all placeholder:text-zinc-600"
      />
    </div>
  );
}


